import React, {useRef} from 'react'

import Image from 'next/image'
import {useRanger, Ranger} from '@tanstack/react-ranger'
import styled from 'styled-components'

import {styledTheme} from '@festi/common/themes'

const Track = styled.div`
  position: relative;
  width: 90%;
  height: 4px;
  user-select: none;
  background: ${styledTheme.palette.ui20Solid};
`

const MainTick = styled.div<{percentage: number}>`
  position: absolute;
  top: 5px;
  left: ${({percentage}) => `${percentage}%`};
  transform: translateX(-50%);
  :before {
    content: '';
    position: absolute;
    left: 0;
    height: 8px;
    width: 1px;
    background: ${styledTheme.palette.ui60Solid};
    transform: translate(-50%, 0.7rem);
  }
`

const Tick = styled.div<{percentage: number}>`
  position: absolute;
  top: 5px;
  left: ${({percentage}) => `${percentage}%`};
  transform: translateX(-50%);
  :before {
    content: '';
    position: absolute;
    left: 0;
    height: 4px;
    width: 1px;
    background: ${styledTheme.palette.ui40Solid};
    transform: translate(-50%, 0.7rem);
  }
`

const TickLabel = styled.div`
  position: absolute;
  top: 100%;
  color: ${styledTheme.palette.ui60Solid};
  transform: translate(-50%, 1.4rem);
  white-space: nowrap;
`

const Segment = styled.div<{index: number; left: number; width: number}>`
  position: absolute;
  height: 100%;
  width: ${(props: {width: number}) => `${props.width}%`};
  left: ${(props: {left: number}) => `${props.left}%`};
  background: ${({index}) => index === 0 && styledTheme.palette.lightBlue};
`

const Handle = styled.button<{active: boolean; left: number}>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  appearance: none;
  border: none;
  outline: none;
  left: ${(props: {left: number}) => `${props.left}%`};
  background: ${styledTheme.palette.lightBlue};
  border-radius: 100%;
  cursor: ${({active}) => (active ? 'grabbing' : 'grab')};
  transform: ${({active}) =>
    active
      ? 'translate(-50%, -50%) scale(1.2)'
      : 'translate(-50%, -50%) scale(1)'};
  :disabled {
    cursor: not-allowed;
  }
`

const HandleIcon = styled(Image)``

interface RangeSliderProps {
  value: number
  min: number
  max: number
  step: number
  mainTicksCount: number
  onChange: (value: number) => void
  disabled?: boolean
}

export default function RangeSlider({
  value,
  min,
  max,
  step,
  mainTicksCount,
  onChange,
  disabled = false,
}: RangeSliderProps) {
  const rangerRef = useRef<HTMLDivElement>(null)

  const allTicks = Array.from(
    {length: (max - min) / step + 1},
    (_, i) => min + i * step,
  )

  function generateMainTicks(
    min: number,
    max: number,
    tickCount: number,
  ): number[] {
    if (tickCount > max) {
      tickCount = max
    }

    const step = (max - min) / (tickCount - 1)

    return Array.from({length: tickCount}, (_, i) => Math.round(min + step * i))
  }

  const mainTicks = generateMainTicks(min, max, mainTicksCount)

  const rangerInstance = useRanger<HTMLDivElement>({
    getRangerElement: () => rangerRef.current,
    values: [value],
    min,
    max,
    stepSize: step,
    ticks: allTicks,
    onChange: (instance: Ranger<HTMLDivElement>) =>
      onChange(instance.sortedValues[0]),
  })

  return (
    <Track ref={rangerRef}>
      {rangerInstance.getTicks().map(({value, key, percentage}) => {
        if (mainTicks.includes(value)) {
          return (
            <MainTick key={key} percentage={percentage}>
              <TickLabel>{value}</TickLabel>
            </MainTick>
          )
        } else {
          return <Tick key={key} percentage={percentage} />
        }
      })}
      {rangerInstance.getSteps().map(({left, width}, i) => (
        <Segment key={i} index={i} left={left} width={width} />
      ))}
      {rangerInstance
        .handles()
        .map(
          (
            {
              value,
              onKeyDownHandler,
              onMouseDownHandler,
              onTouchStart,
              isActive,
            },
            i,
          ) => (
            <Handle
              key={i}
              onKeyDown={onKeyDownHandler}
              onMouseDown={onMouseDownHandler}
              onTouchStart={onTouchStart}
              role="slider"
              aria-valuemin={rangerInstance.options.min}
              aria-valuemax={rangerInstance.options.max}
              aria-valuenow={value}
              left={rangerInstance.getPercentageForValue(value)}
              active={isActive}
              disabled={disabled}
            >
              <HandleIcon
                src="/icons/three-vertical-lines.svg"
                alt="Handle icon"
                width={7}
                height={6}
              />
            </Handle>
          ),
        )}
    </Track>
  )
}
